import React, { useMemo, useCallback, memo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Paper,
} from '@mui/material';

export interface SlotTableRow {
  id: string;
  date: string;              // 日次（YYYY-MM-DD）
  displayDateTime: string;   // 表示用日時（YYYY年M月D日（曜日）HH:MM〜HH:MM）
  recruitmentSlots: number;  // 募集枠
  urgentSlots: number;       // 急募枠
  confirmedSlots: number;    // 確定枠
  shortageSlots: number;     // 不足枠
  isUrgentSet: boolean;      // 急募設定済みかどうか
}

export interface UrgentSettingsSlotTableProps {
  rows: SlotTableRow[];
  selectedIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
}

/**
 * 急募設定スロットテーブルコンポーネント
 *
 * - 時間枠ごとのスロット一覧を表示
 * - 急募設定済みの枠にはチップを表示
 * - 不足枠は赤字で強調表示
 * - チェックボックスで複数選択可能
 * - ヘッダーチェックボックスで全選択/全解除
 */
const UrgentSettingsSlotTable: React.FC<UrgentSettingsSlotTableProps> = memo(({
  rows,
  selectedIds,
  onSelectionChange,
}) => {
  const allSelected = useMemo(() => {
    return rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));
  }, [rows, selectedIds]);

  const someSelected = useMemo(() => {
    return rows.some((row) => selectedIds.includes(row.id)) && !allSelected;
  }, [rows, selectedIds, allSelected]);

  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onSelectionChange(rows.map((row) => row.id));
      } else {
        onSelectionChange([]);
      }
    },
    [rows, onSelectionChange]
  );

  const handleSelectRow = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id));
      } else {
        onSelectionChange([...selectedIds, id]);
      }
    },
    [selectedIds, onSelectionChange]
  );

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={someSelected}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>日次</TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
              募集枠
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
              急募枠
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
              確定枠
            </TableCell>
            <TableCell sx={{ fontWeight: 600, textAlign: 'center', minWidth: 70 }}>
              不足枠
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              hover
              onClick={() => handleSelectRow(row.id)}
              selected={selectedIds.includes(row.id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell padding="checkbox">
                <Checkbox checked={selectedIds.includes(row.id)} />
              </TableCell>
              <TableCell sx={{ fontWeight: 500 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {row.displayDateTime}
                  {row.isUrgentSet && (
                    <Chip label="急募設定済み" color="warning" size="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{row.recruitmentSlots}</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{row.urgentSlots}</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>{row.confirmedSlots}</TableCell>
              <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>
                {row.shortageSlots}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

UrgentSettingsSlotTable.displayName = 'UrgentSettingsSlotTable';

export default UrgentSettingsSlotTable;
